import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ScrollText, ChevronDown, Scale, BookOpen } from "lucide-react";
import { CODICE_CIVILE_ARTICLES } from "@/data/codiceCivileArticles";

/**
 * Static reference page — "Articoli del Codice Civile".
 * All content is embedded (no CMS / DB) so nothing is written to the database.
 * Multiple cards can stay open simultaneously (state = Set of expanded numbers).
 * Reading-optimised for smartphones: single column, generous tap targets.
 */

function ArticleCard({ article, expanded, onToggle }) {
    const label = article.title ? `${article.number} — ${article.title}` : article.number;
    return (
        <li
            data-testid={`codice-card-${article.number.replace(/\W+/g, "-")}`}
            className="rounded-2xl border border-slate-200 bg-white overflow-hidden transition hover:border-primary/30"
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                data-testid={`codice-toggle-${article.number.replace(/\W+/g, "-")}`}
                className="w-full flex items-start gap-3 text-left px-4 py-4 sm:px-5 sm:py-5 hover:bg-slate-50 [&[aria-expanded=true]]:bg-primary/5 transition"
            >
                <span className="inline-flex items-center justify-center shrink-0 size-9 sm:size-10 rounded-xl bg-primary/10 text-primary border border-primary/20">
                    <BookOpen className="size-4 sm:size-[18px]" strokeWidth={2.2} />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block font-serif text-base sm:text-lg font-semibold text-slate-900 leading-snug">
                        {article.number}
                    </span>
                    {article.title && (
                        <span className="mt-0.5 block text-sm text-slate-600 leading-snug">
                            {article.title}
                        </span>
                    )}
                </span>
                <ChevronDown
                    aria-hidden
                    className={`size-5 text-slate-400 shrink-0 mt-1 transition-transform ${expanded ? "rotate-180 text-primary" : ""}`}
                />
                <span className="sr-only">{expanded ? "Chiudi" : "Apri"} {label}</span>
            </button>

            {expanded && (
                <div
                    data-testid={`codice-body-${article.number.replace(/\W+/g, "-")}`}
                    className="px-4 pb-5 sm:px-5 sm:pb-6 border-t border-slate-100 pt-4 sm:pt-5 text-[15px] sm:text-base text-slate-800 leading-relaxed space-y-3"
                >
                    {article.body.map((block, i) =>
                        block.type === "list" ? (
                            <ol key={i} className="list-decimal pl-6 space-y-1.5 marker:text-primary marker:font-semibold">
                                {block.items.map((it, j) => (
                                    <li key={j} className="pl-1">{it}</li>
                                ))}
                            </ol>
                        ) : (
                            <p key={i}>{block.text}</p>
                        )
                    )}
                </div>
            )}
        </li>
    );
}

export default function CodiceCivile() {
    const [expanded, setExpanded] = useState(() => new Set());

    const toggle = (num) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(num)) next.delete(num); else next.add(num);
            return next;
        });
    };

    const codice = CODICE_CIVILE_ARTICLES.filter((a) => a.group === "codice");
    const attuazione = CODICE_CIVILE_ARTICLES.filter((a) => a.group === "attuazione");

    const expandAll = () => setExpanded(new Set(CODICE_CIVILE_ARTICLES.map((a) => a.number)));
    const collapseAll = () => setExpanded(new Set());

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground" data-testid="codice-back">
                <Link to="/"><ArrowLeft className="size-4 mr-1" /> Torna alla home</Link>
            </Button>

            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <Scale className="size-3.5" /> Riferimenti normativi
            </div>
            <h1
                className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight"
                data-testid="codice-title"
            >
                Articoli del Codice Civile
            </h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
                Il testo integrale degli articoli del Codice Civile e delle Disposizioni di attuazione in materia di condominio. Tocca una scheda per aprirla — puoi aprirne più di una alla volta.
            </p>

            {/* Toolbar: expand/collapse only (search rimossa su richiesta) */}
            <div className="mt-6 flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={expandAll}
                    className="flex-1 sm:flex-none rounded-full"
                    data-testid="codice-expand-all"
                >
                    Apri tutti
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={collapseAll}
                    className="flex-1 sm:flex-none rounded-full"
                    data-testid="codice-collapse-all"
                >
                    Chiudi tutti
                </Button>
            </div>

            <div className="mt-3 text-xs text-muted-foreground" data-testid="codice-count">
                {CODICE_CIVILE_ARTICLES.length} articoli
            </div>

            {/* Codice Civile */}
            <section className="mt-8" data-testid="codice-section-codice">
                <div className="flex items-center gap-2 mb-3">
                    <ScrollText className="size-4 text-primary" />
                    <h2 className="font-serif text-xl sm:text-2xl font-semibold text-slate-900 leading-tight">
                        Codice Civile — Libro III, Titolo VII, Capo II
                    </h2>
                </div>
                <ul className="space-y-3">
                    {codice.map((a) => (
                        <ArticleCard
                            key={a.number}
                            article={a}
                            expanded={expanded.has(a.number)}
                            onToggle={() => toggle(a.number)}
                        />
                    ))}
                </ul>
            </section>

            {/* Disposizioni per l'attuazione */}
            <section className="mt-10" data-testid="codice-section-attuazione">
                <div className="flex items-center gap-2 mb-3">
                    <ScrollText className="size-4 text-primary" />
                    <h2 className="font-serif text-xl sm:text-2xl font-semibold text-slate-900 leading-tight">
                        Disposizioni per l'attuazione del Codice Civile e disposizioni transitorie
                    </h2>
                </div>
                <ul className="space-y-3">
                    {attuazione.map((a) => (
                        <ArticleCard
                            key={a.number}
                            article={a}
                            expanded={expanded.has(a.number)}
                            onToggle={() => toggle(a.number)}
                        />
                    ))}
                </ul>
            </section>

            {/* Footer note */}
            <p className="mt-10 text-xs text-muted-foreground leading-relaxed border-t border-slate-200 pt-5">
                <strong>Nota</strong>: il testo degli articoli ha valore informativo. Per casi concreti, consulta un professionista o invia una domanda al nostro esperto.
            </p>

            {/* CTA */}
            <div className="mt-8 rounded-2xl bg-slate-950 text-white p-6 sm:p-8 text-center">
                <h3 className="font-serif text-2xl font-semibold">Un articolo non è chiaro?</h3>
                <p className="mt-2 text-slate-300 text-sm">Chiedi al nostro esperto: risposte private, gratuite e da professionisti reali.</p>
                <Button asChild size="lg" className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full">
                    <Link to="/registrati">Fai una domanda</Link>
                </Button>
            </div>
        </div>
    );
}
